import { useState, useEffect } from 'react';
import {
  NavigationHeader,
  RateSubheader,
  HeroSection,
  AboutSection,
  CollectionsSection,
  GallerySection,
  WhyChooseUsSection,
  TestimonialsSection,
  ContactSection,
  Footer,
  WhatsAppFloatingButton,
  Lightbox,
  LiveMetalPrices,
} from './components';
import { savePreviousRates, getPreviousRates } from './utils/cookieStorage';
import { PriceChange } from './types/metalRates';
import './App.css';

const REFRESH_INTERVAL = 60 * 1000; // 1 minute (same as LiveMetalPrices)

/**
 * Main App Component
 * 
 * Responsibilities:
 * - Manages global state for metal rates and lightbox
 * - Fetches metal rates from /api/metal-prices endpoint every 1 minute
 * - Tracks price changes using cookie storage
 * - Provides lightbox handlers to child components
 * - Renders all sections in correct order with proper section IDs
 * 
 * Requirements: 2.2, 2.3, 12.1, 12.3
 */
function App() {
  // State for metal rates (using /api/metal-prices endpoint)
  const [goldRate24K, setGoldRate24K] = useState<number | null>(null);
  const [silverRatePerKg, setSilverRatePerKg] = useState<number | null>(null);
  const [ratesTimestamp, setRatesTimestamp] = useState<Date | null>(null);
  const [ratesLoading, setRatesLoading] = useState<boolean>(true);
  const [ratesRefreshing, setRatesRefreshing] = useState<boolean>(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState<boolean>(false);

  // State for price changes
  const [goldChange, setGoldChange] = useState<PriceChange | null>(null);
  const [silverChange, setSilverChange] = useState<PriceChange | null>(null);

  // State for lightbox
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);

  // State for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  /**
   * Calculates price change between current and previous rates
   */
  const calculatePriceChange = (current: number, previous: number): PriceChange => {
    const amount = current - previous;
    const percentage = (amount / previous) * 100;
    
    let direction: 'up' | 'down' | 'neutral' = 'neutral';
    if (amount > 0) direction = 'up';
    else if (amount < 0) direction = 'down';

    return { amount, percentage, direction };
  };

  /**
   * Loads metal rates from /api/metal-prices endpoint
   */
  const loadMetalRates = async (isRefresh: boolean = false) => {
    if (isRefresh) {
      setRatesRefreshing(true);
    } else {
      setRatesLoading(true);
    }
    
    setRatesError(null);
    setUsingFallback(false);

    try {
      const response = await fetch('/api/metal-prices');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();

      // Get previous rates from cookies for comparison
      const previousRates = getPreviousRates();

      // Calculate price changes if previous rates exist
      if (previousRates) {
        const goldPriceChange = calculatePriceChange(data.gold_24k, previousRates.gold);
        const silverPriceChange = calculatePriceChange(data.silver_per_kg / 1000, previousRates.silver);
        
        setGoldChange(goldPriceChange);
        setSilverChange(silverPriceChange);
      } else {
        // First visit - no previous data
        setGoldChange(null);
        setSilverChange(null);
      }

      // Update state with new rates
      setGoldRate24K(data.gold_24k);
      setSilverRatePerKg(data.silver_per_kg);
      setRatesTimestamp(new Date(data.updated_at));

      // Save current rates as previous for next comparison (convert silver to per gram for consistency)
      savePreviousRates(data.gold_24k, data.silver_per_kg / 1000, new Date(data.updated_at));
    } catch (error) {
      console.error('Error loading metal rates:', error);
      
      // Try fallback to cookies
      const fallbackRates = getPreviousRates();
      
      if (fallbackRates) {
        setGoldRate24K(fallbackRates.gold);
        setSilverRatePerKg(fallbackRates.silver * 1000); // Convert per gram to per kg
        setRatesTimestamp(new Date(fallbackRates.timestamp));
        setUsingFallback(true);
        setRatesError('Using cached rates');
      } else {
        setRatesError('Unable to fetch current rates');
      }
    } finally {
      setRatesLoading(false);
      setRatesRefreshing(false);
    }
  };

  // Fetch metal rates on component mount and set up auto-refresh
  useEffect(() => {
    // Initial load
    loadMetalRates(false);

    // Set up auto-refresh every 1 minute (same as LiveMetalPrices)
    const interval = setInterval(() => {
      loadMetalRates(true);
    }, REFRESH_INTERVAL);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lightbox handlers
  const openLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Navigation Header */}
      <NavigationHeader onMenuStateChange={setIsMobileMenuOpen} />

      {/* Rate Subheader */}
      <RateSubheader
        goldRate={goldRate24K}
        silverRate={silverRatePerKg ? silverRatePerKg / 1000 : null}
        timestamp={ratesTimestamp}
        loading={ratesLoading}
        refreshing={ratesRefreshing}
        error={ratesError}
        usingFallback={usingFallback}
      />

      {/* Hero Section */}
      <section id="home">
        <HeroSection 
          goldRate={goldRate24K} 
          silverRate={silverRatePerKg ? silverRatePerKg / 1000 : null}
          goldChange={goldChange}
          silverChange={silverChange}
        />
      </section>

      {/* About Section */}
      <section id="about">
        <AboutSection />
      </section>

      {/* Collections Section */}
      <section id="collections">
        <CollectionsSection onImageClick={openLightbox} />
      </section>

      {/* Gallery Section */}
      <section id="gallery">
        <GallerySection onImageClick={openLightbox} />
      </section>

      {/* Rates Section - Live Market Prices */}
      <section id="rates">
        <LiveMetalPrices />
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us">
        <WhyChooseUsSection />
      </section>

      {/* Testimonials Section */}
      <section id="testimonials">
        <TestimonialsSection />
      </section>

      {/* Contact Section */}
      <section id="contact">
        <ContactSection />
      </section>

      {/* Footer */}
      <Footer />

      {/* WhatsApp Floating Button - Hidden when mobile menu is open */}
      <WhatsAppFloatingButton isHidden={isMobileMenuOpen} />

      {/* Lightbox Modal */}
      <Lightbox
        imageUrl={lightboxImage}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
      />
    </div>
  );
}

export default App;
